<template>
  <div class="map-widget-container">
    <div class="map-header">
      <div class="title">Capas</div>
      <div class="toggles">
  <v-switch class="layer-switch" density="compact" hide-details inset :class="{ active: uiVisibility.incidents }" label="Incidencias" v-model="uiVisibility.incidents" />
  <!-- P13-V7B · the "Supervisores" layer is retired from this widget. The reader it draws from
       publishes no position for anybody, so the layer could never hold a marker: a switch that
       offered a capability with no producer. Positions are V4 (GPS) capability and are not built
       here. -->
  <v-switch class="layer-switch" density="compact" hide-details inset :class="{ active: uiVisibility.agencies }" label="Agencias" v-model="uiVisibility.agencies" />
  <v-switch class="layer-switch" density="compact" hide-details inset :class="{ active: uiVisibility.routes }" label="Rutas" v-model="uiVisibility.routes" />
      </div>
      <div class="actions">
        <v-btn icon="mdi-refresh" size="x-small" variant="text" @click="refreshMap" :loading="loading" />
      </div>
    </div>
    

  <div class="map-content">
      <!-- Controles avanzados ocultos en la vista rápida -->

      <div ref="mapContainer" class="map-element"></div>

      <div v-if="loading" class="map-loading-overlay">
        <v-progress-circular indeterminate size="36" color="primary" />
      </div>

      <div v-if="error" class="map-error-overlay">
        <v-icon size="28" color="error" class="mb-2">mdi-alert-circle</v-icon>
        <div class="error-text">{{ error }}</div>
        <v-btn size="x-small" variant="outlined" color="error" @click="retryLoad">
          Reintentar
        </v-btn>
      </div>

      <div v-if="showLegend" class="legend-flyout">
        <MapLegend :layers="legendLayers" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
let L: any
import MapControls from './MapControls.vue'
import MapLegend from './MapLegend.vue'
import { useMapData } from '../composables/useMapData'
import { useMapLayers } from '../composables/useMapLayers'

const mapContainer = ref<HTMLElement | null>(null)
const map = ref<any | null>(null)
const showControls = ref(false)
const showLegend = ref(false)
const coordinates = ref<{ lat: number; lng: number } | null>(null)

const {
  filteredIncidents,
  filteredSupervisors,
  filteredAgencies,
  filteredRoutes,
  loading,
  error,
  statistics,
  availableCategories,
  availableZones,
  availableSupervisors,
  loadAllData,
  refreshData
} = useMapData()

// Inicializamos el composable de capas DESPUÉS de crear el mapa.
const layersApi = ref<ReturnType<typeof useMapLayers> | null>(null)
const layerVisibility = computed(() => (layersApi.value?.layerVisibility as any)?.value ?? {
  incidents: true,
  supervisors: true,
  agencies: true,
  routes: false,
  clustering: false,
  heatmap: false
})
const layerFilters = computed(() => (layersApi.value?.layerFilters as any)?.value ?? {
  incidents: { priorities: [], statuses: [], categories: [] },
  supervisors: { statuses: [], zones: [] },
  agencies: { statuses: [], zones: [] },
  routes: { statuses: [] }
})

// Capas visibles para la leyenda (excluye controles como clustering/heatmap)
const legendLayers = computed(() => {
  const lv = (layersApi.value?.layerVisibility as any)?.value || {
    incidents: true,
    supervisors: true,
    agencies: true,
    routes: false
  }
  const layers: string[] = []
  if (lv.incidents) layers.push('incidents')
  if (lv.agencies) layers.push('agencies')
  if (lv.routes) layers.push('routes')
  return layers
})

const isFullscreen = ref(false)
const mapHeight = computed(() => '100%')

// Visibilidad UI sincronizada con API de capas
const uiVisibility = ref({ incidents: true, supervisors: false, agencies: true, routes: false })

watch(uiVisibility, (nv) => {
  layersApi.value?.setLayerVisibility('incidents' as any, nv.incidents)
  layersApi.value?.setLayerVisibility('supervisors' as any, nv.supervisors)
  layersApi.value?.setLayerVisibility('agencies' as any, nv.agencies)
  layersApi.value?.setLayerVisibility('routes' as any, nv.routes)
  updateAllLayers()
}, { deep: true })

watch(
  () => (layersApi.value?.layerVisibility as any)?.value,
  (lv) => {
    if (!lv) return
    uiVisibility.value = {
      incidents: !!lv.incidents,
      supervisors: false,
      agencies: !!lv.agencies,
      routes: !!lv.routes,
    }
  },
  { deep: true }
)

const initializeMap = async () => {
  if (!mapContainer.value) return
  if (!L) {
    const leafletModule = await import('leaflet')
    L = (leafletModule as any).default || leafletModule
    await import('leaflet/dist/leaflet.css')
    try {
      delete (L.Icon.Default.prototype as any)._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
      })
    } catch {}
  }

  map.value = L.map(mapContainer.value, {
    center: [18.7357, -70.1627],
    zoom: 7,
    maxZoom: 18,
    minZoom: 6,
    zoomControl: false,
    attributionControl: false,
    preferCanvas: true,
    // Reducir animaciones para evitar drift visual de marcadores
    zoomAnimation: true,
    markerZoomAnimation: false,
    fadeAnimation: true
  })

  const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 18,
    // Evitar discrepancias de timing entre tiles y marcadores durante zoom
    updateWhenZooming: false,
    updateWhenIdle: true
  })
  tileLayer.addTo(map.value)

  map.value.on('mousemove', (e: any) => {
    coordinates.value = { lat: e.latlng.lat, lng: e.latlng.lng }
  })
  map.value.on('mouseout', () => { coordinates.value = null })

  // Forzar re-render de capas tras finalizar el zoom para evitar desalineación visual
  map.value.on('zoomend', () => {
    try {
      layersApi.value?.incidentLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
      layersApi.value?.supervisorLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
      layersApi.value?.agencyLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
      layersApi.value?.routeLayers?.value?.eachLayer?.((l: any) => l.redraw?.())
    } catch {}
  })

  await nextTick()
  try {
    const { setLeafletModule } = await import('../composables/useMapLayers')
    setLeafletModule(L as any)
  } catch {}
  // Crear API de capas con el mapa ya inicializado y luego inicializar
  layersApi.value = useMapLayers(map.value, { popupMode: 'compact', enablePopups: false })
  // Bloquear cualquier click en entidades si no hay popups habilitados
  map.value.on('click', (e: any) => {
    e.originalEvent?.stopPropagation?.()
  })
  layersApi.value?.initializeLayers()
  // Inicializar switches desde estado real
  // P13-V7B: the supervisor layer is switched OFF in this widget (it has no position producer).
  layersApi.value?.setLayerVisibility('supervisors' as any, false)
  const lv = (layersApi.value?.layerVisibility as any)?.value || {}
  uiVisibility.value = {
    incidents: lv.incidents ?? true,
    supervisors: false,
    agencies: lv.agencies ?? true,
    routes: lv.routes ?? false
  }
}

const updateAllLayers = () => {
  if (!layersApi.value) return
  layersApi.value.updateIncidentLayer(filteredIncidents.value)
  layersApi.value.updateAgencyLayer(filteredAgencies.value)
  layersApi.value.updateRouteLayer(filteredRoutes.value)
}

const handleLayerToggle = (layer: string, visible: boolean) => {
  layersApi.value?.setLayerVisibility(layer as any, visible)
  // Si cambia clustering, el watcher reconstruye la capa; repoblar luego
  if (layer === 'clustering') {
    updateAllLayers()
  }
}

const handleFilterChange = (filters: any) => {
  layersApi.value?.updateLayerFilters(filters)
  updateAllLayers()
}

const handleSearch = (_q: string) => {}

const refreshMap = async () => {
  await refreshData()
  updateAllLayers()
}

const retryLoad = async () => {
  await loadAllData(true)
  updateAllLayers()
}

onMounted(async () => {
  await initializeMap()
  await loadAllData()
  updateAllLayers()
})

onUnmounted(() => {
  if (map.value) {
    map.value.remove()
    map.value = null
  }
})

watch([showControls], () => {
  nextTick(() => { map.value?.invalidateSize() })
})
</script>

<style scoped>
.map-widget-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.map-content {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  padding-top: 6px;
}
.map-element {
  width: 100%;
  height: 100%;
}
.map-loading-overlay, .map-error-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.7);
}
.controls-flyout {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1000;
}
.legend-flyout {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 1000;
}
.map-toolbar.compact {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1000;
  display: flex;
  width: calc(100% - 16px);
  justify-content: space-between;
}
.map-header {
  position: sticky;
  top: 0;
  z-index: 1100;
  background: rgba(255,255,255,0.9);
  backdrop-filter: blur(6px);
  border-bottom: 1px solid #e2e8f0;
  border-radius: 8px 8px 0 0;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
}
.map-header .title { font-weight: 600; color: #334155; }
.map-header .toggles { display: flex; gap: 8px; flex-wrap: wrap; }

.layer-switch :deep(.v-selection-control) { min-height: 24px; }
.layer-switch :deep(.v-switch__track) { height: 16px; }
.layer-switch :deep(.v-switch__thumb) { width: 14px; height: 14px; }
.layer-switch :deep(.v-label) { font-size: 12px; color: #475569; }

.layer-switch.active :deep(.v-switch__track) { background-color: #4f46e5 !important; opacity: 0.25; }
.layer-switch.active :deep(.v-switch__thumb) { background-color: #4f46e5 !important; box-shadow: 0 0 0 2px rgba(79,70,229,.2); }
.layer-switch.active :deep(.v-label) { color: #1f2937; font-weight: 600; }

:deep(.leaflet-popup-content-wrapper) {
  max-width: min(90vw, 320px);
  max-height: 60vh;
}
:deep(.leaflet-popup-content) { max-height: 60vh; }

:deep(.mgi-popup--compact) { font-size: 11px; }
:deep(.mgi-popup--compact h4) { font-size: 0.9rem !important; }
:deep(.mgi-popup--compact p) { font-size: 11px !important; line-height: 1.25 !important; }
:deep(.mgi-popup--compact button) { font-size: 10.5px !important; padding: 5px 10px !important; border-radius: 14px !important; }

:deep(.leaflet-interactive),
:deep(.leaflet-marker-icon),
:deep(.custom-map-icon) {
  cursor: default !important;
}

/* Evitar que estilos heredados afecten al posicionamiento de los íconos */
:deep(.custom-map-icon) {
  will-change: top, left;
  transform: translateZ(0);
  transition: none !important;
}
</style>
